import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { List, MagnifyingGlassPlus, PaperPlaneTilt, X } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Sidebar from '../components/Sidebar';
import ChatMessage, { SparkleIcon } from '../components/ChatMessage';
import ChatErrorBoundary from '../components/ChatErrorBoundary';
import ItemDetailModal from '../components/ItemDetailModal';
import ChatProgress from '../components/ChatProgress';
import useChatProgress from '../hooks/useChatProgress';
import { getItemHeadline, getItemCategory } from '../utils/itemHelpers';

// COL-74: there's no ordering guarantee between the GET that loads history
// and a concurrent POST /chat that's still writing it — the checkpoint write
// for a user's message can commit while the GET is still in flight, so the
// "optimistic" copy already sitting in local state and the server's own copy
// can both end up in the merged array. Only the leading run of `localTail`
// can ever be server-echoed this way (anything added to local state was
// appended in send order, oldest first, same as `loaded`), so walk from the
// largest possible overlap down to zero and drop the first exact (role +
// content) match. Local messages that were never sent to the server yet
// (e.g. an assistant reply that hasn't arrived) simply won't match anything
// and are kept as-is.
function dropServerEchoedPrefix(loaded, localTail) {
  const maxOverlap = Math.min(loaded.length, localTail.length);
  for (let n = maxOverlap; n > 0; n--) {
    const loadedSuffix = loaded.slice(loaded.length - n);
    const localPrefix = localTail.slice(0, n);
    const matches = loadedSuffix.every((m, i) =>
      m.role === localPrefix[i].role && m.content === localPrefix[i].content
    );
    if (matches) return localTail.slice(n);
  }
  return localTail;
}

/**
 * Full-page chat experience at /chat.
 * Uses the app-layout pattern (sidebar + main content) like Gallery/Entities.
 */
export default function Chat() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // COL-87: /chat/:sessionId makes a conversation restorable from its URL.
  // Seeding activeSessionId from the route param (rather than always
  // starting null) means a deep link lands on that session instead of
  // being clobbered by the "auto-select most recent" logic below.
  const { sessionId: sessionIdParam } = useParams();

  // Chat state
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(() => sessionIdParam || null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  // COL-39: token for the in-flight send's staged status poll. null whenever
  // nothing is in flight (matches useChatProgress's own idle state).
  const [progressToken, setProgressToken] = useState(null);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  // Pinned item session context (COL-93): { item_id, headline, category,
  // thumbnail } | null. Set optimistically from local item data the moment
  // "Chat about this" fires, then overwritten with the server's copy once
  // the first message confirms the pin.
  const [pinnedItem, setPinnedItem] = useState(null);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const activeSessionIdRef = useRef(activeSessionId);
  const itemContextHandledRef = useRef(false);
  const hasLoadedInitialSession = useRef(false);
  const latestSessionsRequest = useRef(0);
  const latestSend = useRef(0);
  // COL-74: guards selectSession's history fetch against a second concurrent
  // call for the same session — see the comment at its use for why
  // activeSessionIdRef alone isn't enough once loading merges instead of
  // replacing.
  const latestHistoryRequest = useRef(0);
  // COL-73: true whenever activeSessionId is null because the user
  // deliberately started a blank conversation (startNewSession) that hasn't
  // been sent yet — as opposed to null because nothing has been selected at
  // all yet (initial mount, before the session list has loaded). Only the
  // latter should let loadSessions' auto-select pick the most recent
  // session; the former must be left alone, or a background refresh landing
  // in that window (e.g. from an earlier send in a different session still
  // resolving when "New conversation" was clicked) would fork the blank
  // compose state back onto whatever session is now most recent. Reset to
  // false the moment activeSessionId becomes non-null again for any reason
  // (selecting a session, or a send adopting a server-minted id).
  const explicitlyNewSessionRef = useRef(false);
  // Item id to attach to the NEXT sendMessage call only. Set when "Chat
  // about this" starts a new session (pinnedItem is shown locally before
  // anything is persisted); cleared once that first send has carried it to
  // the server, since the pin is durable there from then on (COL-93).
  const pendingPinItemIdRef = useRef(null);
  // COL-39: which session progressToken's send belongs to. Progress state
  // (progressToken/events) is entirely separate from `messages` — it can
  // never touch setMessages and so can never resurrect the COL-74 history
  // race — but without this it WOULD leak a stale send's session-specific
  // status text ("Searching for 'ramen'…") into whatever session the user
  // switches to next, since isLoading alone is session-agnostic. Compared
  // against activeSessionId (reactive) below to hide progress the moment
  // the viewed session no longer matches, while the actual send keeps
  // running unaffected in the background.
  const progressSessionIdRef = useRef(null);

  // COL-39: only poll/show progress while a send is in flight AND the user
  // is still looking at the session it belongs to (see progressSessionIdRef
  // above). Also used at render time below to blank progressEvents for one
  // extra render — this flips false in the same render `activeSessionId`
  // changes in, but the hook's own clear-on-disable runs in an effect after
  // that render commits, which would otherwise let one frame of the old
  // session's status text flash into the new session's bubble.
  const isViewingSendsSession = progressSessionIdRef.current === activeSessionId;
  const { events: progressEvents } = useChatProgress(progressToken, {
    enabled: isLoading && isViewingSendsSession,
  });

  // Keep ref in sync with state (for race condition prevention)
  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  // Keep the URL in sync with whichever session is actually active — server
  // id adoption after a send, auto-select-most-recent, an explicit switch,
  // or the deep-link seed above. Always a replace: chat session switches
  // are frequent and largely automatic, so pushing one per switch would
  // turn browser back into a session history the user never asked for.
  useEffect(() => {
    if (!activeSessionId || sessionIdParam === activeSessionId) return;
    navigate(`/chat/${activeSessionId}`, { replace: true });
  }, [activeSessionId, sessionIdParam, navigate]);

  // The reverse direction: the URL's :sessionId changed under us (browser
  // back/forward, or a pasted link while already on /chat/:otherId — same
  // route component, so no remount) to a session that isn't the one
  // currently loaded. Load its messages.
  useEffect(() => {
    if (!sessionIdParam || sessionIdParam === activeSessionIdRef.current) return;
    selectSession(sessionIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam]);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on mount
  useEffect(() => {
    const timeoutId = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(timeoutId);
  }, []);

  // Load sessions. showLoading=false for background refreshes to avoid sidebar flash.
  const loadSessions = useCallback(async (showLoading = true) => {
    const requestId = ++latestSessionsRequest.current;
    try {
      if (showLoading) setIsLoadingSessions(true);
      const response = await api.getChatSessions();
      // Drop a response the mount load and a post-send refresh raced on: the
      // stale list can be missing the session just created, which is the exact
      // "my chat vanished" symptom this page is meant to fix.
      if (requestId !== latestSessionsRequest.current) return;
      setSessions(response.sessions || []);

      // Auto-select most recent session if none selected. A null
      // activeSessionId is deliberate (see explicitlyNewSessionRef) once the
      // user has started a blank conversation — don't override it (COL-73).
      if (response.sessions?.length > 0) {
        setActiveSessionId(prev => {
          if (prev) return prev;
          if (explicitlyNewSessionRef.current) return prev;
          return response.sessions[0].session_id;
        });
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      // Only the initial load reports into the transcript. A background
      // refresh that blips would otherwise drop "failed to load" under a
      // perfectly good reply, or into whatever session the user switched to.
      if (showLoading) {
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: 'Failed to load conversations. Please refresh the page.',
        }]);
      }
    } finally {
      if (showLoading) setIsLoadingSessions(false);
    }
  }, []);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load messages when activeSessionId changes (including auto-select from
  // loadSessions, or the URL-seeded id on a COL-87 deep link). Waits for
  // isLoadingSessions to clear before judging "existing vs. brand-new":
  // a URL-seeded activeSessionId is set on the very first render, before
  // `sessions` has loaded, and without this guard that first run would see
  // an empty `sessions` array, wrongly conclude "brand-new session", and
  // permanently latch hasLoadedInitialSession — so the real session's
  // history never loads once `sessions` actually arrives a moment later.
  useEffect(() => {
    if (!activeSessionId || isLoadingSessions) return;
    // Skip if this is a brand-new session (no server-side messages to load)
    const isExistingSession = sessions.some(s => s.session_id === activeSessionId);
    if (!isExistingSession) {
      hasLoadedInitialSession.current = true;
      return;
    }
    // Only auto-load on first selection or when user hasn't manually selected yet
    if (!hasLoadedInitialSession.current) {
      hasLoadedInitialSession.current = true;
      selectSession(activeSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, sessions, isLoadingSessions]);

  // Single entry point for "start a blank conversation", used by the New
  // conversation button and pinItemAndStartNewSession (COL-93). COL-73: the
  // server is the sole owner of session ids — there is nothing to mint here.
  // activeSessionId goes to null, which is a valid, deliberate state (see
  // explicitlyNewSessionRef) until the first message on this conversation
  // gets a server-minted id back.
  const startNewSession = useCallback(() => {
    activeSessionIdRef.current = null; // Sync ref so an in-flight reply can't land here
    hasLoadedInitialSession.current = true; // Nothing to restore for a fresh session
    explicitlyNewSessionRef.current = true; // Guards loadSessions' auto-select — see its declaration
    setActiveSessionId(null);
    setMessages([]);
    setSelectedItem(null);
    // A blank session starts with no pinned context — any pin belongs to
    // whatever conversation was previously being composed (COL-93).
    setPinnedItem(null);
    pendingPinItemIdRef.current = null;
    // An in-flight send from the previous conversation must not leave the new
    // one disabled behind a typing indicator that belongs to a thread the user
    // has already left; its reply is discarded by the session ref guard.
    // Bumping the token first disowns that send, so when it finally settles it
    // can't clear the loading state of whatever is running by then.
    latestSend.current += 1;
    setIsLoading(false);
    // COL-39: same disowning — a stale progress token/session pairing must
    // not carry into the new session (isLoading=false already hides it, but
    // clearing here too keeps state consistent rather than relying on that
    // alone).
    setProgressToken(null);
    progressSessionIdRef.current = null;
    // The URL-sync effect below only fires for a non-null activeSessionId —
    // it has nowhere to route a session that doesn't exist yet, so unlike
    // every other activeSessionId change, going to null needs an explicit
    // navigate here to clear a stale session id out of the address bar.
    // Also clears location.state: an item-context deep link (COL-93/COL-75)
    // may have brought us here, and its pinItemAndStartNewSession call below
    // no longer has an id of its own to navigate to and consume it with.
    navigate('/chat', { replace: true, state: {} });
    // Focus after the disabled attribute has actually cleared in the DOM.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [navigate]);

  // "Chat about this item" (COL-93): starts a blank session and shows the
  // pinned chip immediately from the item data already on hand — no message
  // is sent automatically, the user types their own first question. Used by
  // both the Gallery deep link and Chat's own item modal, so both entry
  // points share one implementation. COL-73: there's no new session id to
  // return anymore — startNewSession already navigates to bare /chat itself.
  const pinItemAndStartNewSession = useCallback((item) => {
    const itemId = item.item_id || item.id;
    if (!itemId) return;
    startNewSession();
    setPinnedItem({
      item_id: itemId,
      headline: getItemHeadline(item),
      category: getItemCategory(item),
      thumbnail: item.thumbnail || item.image || null,
    });
    // Carried on the next sendMessage call only; the server persists the
    // pin from there (see sendMessage).
    pendingPinItemIdRef.current = itemId;
  }, [startNewSession]);

  const selectSession = async (sessionId) => {
    // Two concurrent calls can target the *same* sessionId (e.g. React
    // StrictMode's dev-only double effect invocation, or a fast double-click
    // on the sidebar) — activeSessionIdRef alone can't tell those apart,
    // since both calls are for the currently-active session. Without this
    // token, each resolution independently appends the loaded history ahead
    // of whatever's in `messages` at the time, and two concurrent appends
    // duplicate it. Only the newest call's resolution is allowed to touch
    // state; a superseded one is a no-op even if it lands.
    const requestId = ++latestHistoryRequest.current;
    activeSessionIdRef.current = sessionId; // Sync ref before async API call
    explicitlyNewSessionRef.current = false; // Selecting a real session ends any blank-compose state (COL-73)
    setActiveSessionId(sessionId);
    setMessages([]); // Clear immediately while loading
    setSelectedItem(null); // Close any open item modal
    setPinnedItem(null); // Cleared until the load below confirms one either way
    pendingPinItemIdRef.current = null; // Switching sessions abandons any unsent pin

    try {
      const response = await api.getChatSessionMessages(sessionId);
      if (activeSessionIdRef.current === sessionId && requestId === latestHistoryRequest.current) {
        // Items come from the server's structured contract (COL-38), fully
        // enriched — same shape as a live reply. Sessions from before that
        // contract return items: [] and render as text only.
        const loadedMessages = (response.messages || []).map((msg, i) => ({
          // The `history-` prefix is load-bearing: ChatMessage keys the
          // enter-animation skip off it.
          id: `history-${i}`,
          role: msg.role,
          content: msg.content,
          items: msg.items || [],
        }));
        // COL-74: this fetch can take a while, and the composer isn't gated
        // on it (only on isLoadingSessions). If the user typed and sent a
        // message during the wait, sendMessage's optimistic append (and its
        // reply, if it lands first too) is sitting in `messages` right now —
        // `messages` was cleared to [] at the top of this function, so
        // anything in `prev` was necessarily added after that, i.e. during
        // this fetch. A plain `setMessages(loadedMessages)` would silently
        // discard it (the original bug); append it after the loaded history
        // instead of replacing.
        setMessages(prev => [...loadedMessages, ...dropServerEchoedPrefix(loadedMessages, prev)]);
        // Pinned item context survives a reload (COL-93) because it's read
        // back from the server here, not carried in client/router state.
        setPinnedItem(response.pinned_item || null);
      }
    } catch (err) {
      console.error('Failed to load session messages:', err);
      if (activeSessionIdRef.current === sessionId && requestId === latestHistoryRequest.current) {
        // Same append-not-replace reasoning as the success path: don't erase
        // a message the user sent while the (now-failed) history fetch was
        // still in flight.
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: 'Failed to load conversation history. Try selecting another conversation.',
        }]);
      }
    }
  };

  const deleteSession = async (sessionId, e) => {
    if (e) e.stopPropagation();
    if (deletingSessionId) return; // Prevent double-click
    setDeletingSessionId(sessionId);
    try {
      await api.deleteChatSession(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSessionId === sessionId) {
        startNewSession();
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error',
        content: 'Failed to delete conversation. Please try again.',
      }]);
    } finally {
      setDeletingSessionId(null);
    }
  };

  const sendMessage = useCallback(async (textOverride = null, sessionIdOverride = null) => {
    const messageText = textOverride || inputValue;
    if (!messageText.trim() || isLoading) return;

    const userMessage = messageText.trim();
    const sessionIdAtSend = sessionIdOverride ?? activeSessionId;
    const sendId = ++latestSend.current;
    setInputValue('');

    // Carried to the server on this send only (COL-93) — consumed here so a
    // second message fired before this one resolves can't attach the pin
    // twice. Idempotent either way (the pin is an upsert), this just keeps
    // the intent to "pin once" honest.
    const pinItemIdForThisSend = pendingPinItemIdRef.current;
    pendingPinItemIdRef.current = null;

    // Add user message immediately
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'user',
      content: userMessage,
    }]);

    // COL-39: minted fresh per send, before isLoading flips — a poll that
    // starts as soon as the composer disables must never be watching a
    // stale token from a previous turn. progressSessionIdRef records which
    // session this belongs to (see its declaration for why).
    const token = crypto.randomUUID();
    progressSessionIdRef.current = sessionIdAtSend;
    setProgressToken(token);
    setIsLoading(true);

    try {
      const response = await api.sendMessage(
        userMessage,
        sessionIdAtSend,
        pinItemIdForThisSend || null,
        token
      );

      if (activeSessionIdRef.current === sessionIdAtSend) {
        // The server is the source of truth for what's pinned on this
        // session — sync every time rather than only on the turn that set
        // it, so a stale/self-healed pin (COL-80: the item was deleted)
        // clears the chip too.
        setPinnedItem(response.pinned_item || null);
        // The server owns the session id: when we sent none it minted one, and
        // keeping our null here would fork a fresh thread on every message
        // (COL-36). Adopt it, and mark the initial load as done so the
        // background session refresh below doesn't re-fetch this conversation
        // and wipe the reply we're about to render.
        const serverSessionId = response.session_id;
        if (serverSessionId && serverSessionId !== sessionIdAtSend) {
          activeSessionIdRef.current = serverSessionId;
          hasLoadedInitialSession.current = true;
          // A real id now exists — loadSessions' auto-select no longer needs
          // to leave this alone (COL-73).
          explicitlyNewSessionRef.current = false;
          setActiveSessionId(serverSessionId);
        } else if (!serverSessionId && !sessionIdAtSend) {
          // Nothing to adopt and nothing to keep: every further message would
          // silently mint another server thread. Loud enough to notice if the
          // response shape ever regresses.
          console.warn('Chat response carried no session_id; conversation cannot be continued');
        }

        // items[] is the contract (COL-38): the server returns every item the
        // reply was built from, already enriched. The reply text is prose —
        // the model is instructed not to inline item IDs or JSON — so there
        // is nothing to excavate out of it here.
        const responseItems = response.items || [];
        // Skip an assistant bubble with nothing to show — matches reload
        // behavior, which already drops empty-content messages (COL-364).
        // The backend fallback (agentic_chat.py) means this should be rare,
        // but a truly empty response + no items is still not worth a bubble.
        if (response.response || responseItems.length > 0) {
          setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: response.response || '',
            items: responseItems,
            toolCalls: response.tool_calls || [],
          }]);
        }
      }

      loadSessions(false); // Background refresh — don't flash sidebar loading state
    } catch (err) {
      // A send that was carrying the pin failed before the server ever saw
      // it — restore the pending id so the next attempt (the user retyping
      // and resending) still tries to establish the pin, instead of the
      // chip silently claiming a pin that was never persisted (COL-93).
      if (pinItemIdForThisSend && activeSessionIdRef.current === sessionIdAtSend) {
        pendingPinItemIdRef.current = pinItemIdForThisSend;
      }
      if (activeSessionIdRef.current === sessionIdAtSend) {
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: 'Failed to send message. Please try again.',
        }]);
      }
    } finally {
      // Only the newest send owns the loading state. A send the user walked
      // away from settles late, and clearing the flag then would re-enable the
      // composer underneath a send that is still running - letting a second
      // concurrent POST into the same session, since the send guard reads
      // isLoading alone.
      if (sendId === latestSend.current) {
        setIsLoading(false);
        // COL-39: same guard — an abandoned send's progress must not clear
        // the token a newer send just set.
        setProgressToken(null);
      }
    }
  }, [inputValue, isLoading, activeSessionId, loadSessions]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Same reason as the composer: sending before the session list lands lets
    // the auto-select wipe the message before its reply arrives.
    if (isLoading || isLoadingSessions) return;
    sendMessage(suggestion);
  };

  // Handle item click from chat message cards — fetch full item for modal
  const handleItemClick = useCallback((item) => {
    const itemId = item.item_id || item.id;
    setSelectedItem(item);

    // Only fetch if we don't already have full item data (ChatItemCard passes cached full item)
    if (itemId && !item.image) {
      api.getItem(itemId)
        .then((fullItem) => {
          setSelectedItem((current) => {
            if (!current) return null;
            const currentId = current.item_id || current.id;
            return currentId === itemId ? fullItem : current;
          });
        })
        .catch((err) => {
          console.error('Failed to load full item details:', err);
        });
    }
  }, []);

  // Unpin the session's item (COL-93). A pin the server has never seen
  // (still only local, pre-first-send) just clears local state; a confirmed
  // one needs the server told, or it would reappear on the next reload.
  // Blocked while a send is in flight: that send may itself be the one
  // establishing the pin (its response still sets pinnedItem unconditionally
  // once it lands), so unpinning underneath it would silently get undone by
  // whichever of the two requests resolves last.
  const handleUnpin = useCallback(async () => {
    if (isLoading) return;
    const wasConfirmedByServer = !pendingPinItemIdRef.current;
    setPinnedItem(null);
    pendingPinItemIdRef.current = null;

    if (wasConfirmedByServer && activeSessionId) {
      try {
        await api.unpinChatItem(activeSessionId);
      } catch (err) {
        console.error('Failed to unpin item:', err);
      }
    }
  }, [activeSessionId, isLoading]);

  // Handle deep link: navigate to /chat with itemContext state ("Chat about
  // this item" from Gallery). COL-93 replaced the canned-prompt auto-send
  // this used to do (a 500ms setTimeout raced navigate()'s location.state
  // mutation — the effect's own dependency — so React tore the timer down
  // via cleanup before it ever fired; COL-75 is the same bug). There is
  // nothing left to race: pinning is synchronous local state, and no
  // message is sent until the user writes one. COL-73: no id exists yet to
  // build a session URL from — pinItemAndStartNewSession (via
  // startNewSession) already lands on bare /chat and clears location.state
  // itself, so there's nothing left to do here after handling the item.
  useEffect(() => {
    if (!location.state?.itemContext || itemContextHandledRef.current) return;
    itemContextHandledRef.current = true;
    pinItemAndStartNewSession(location.state.itemContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Sidebar chat config
  const chatConfig = {
    sessions,
    activeSessionId,
    isLoading: isLoadingSessions,
    onSelect: selectSession,
    onNew: startNewSession,
    onDelete: deleteSession,
  };

  return (
    <div className="app-layout">
      <Sidebar
        chatConfig={chatConfig}
        user={user}
        onLogout={logout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="main-content chat-page">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </button>
            <h1 className="page-title">Chat</h1>
          </div>
        </header>

        <ChatErrorBoundary onClose={() => {}}>
          <div className="chat-page-messages">
            {messages.length === 0 ? (
              <div className="chat-page-welcome">
                <div className="chat-welcome-icon">
                  <SearchSparkleIcon />
                </div>
                <h3>Explore your collection</h3>
                <p>Ask questions about your screenshots and I'll help you find what you're looking for.</p>

                <div className="chat-suggestions">
                  <button
                    className="chat-suggestion"
                    onClick={() => handleSuggestionClick('What items do I have about restaurants?')}
                  >
                    What items do I have about restaurants?
                  </button>
                  <button
                    className="chat-suggestion"
                    onClick={() => handleSuggestionClick('Show me screenshots from this week')}
                  >
                    Show me screenshots from this week
                  </button>
                  <button
                    className="chat-suggestion"
                    onClick={() => handleSuggestionClick('Find anything related to travel')}
                  >
                    Find anything related to travel
                  </button>
                </div>
              </div>
            ) : (
              <div className="chat-messages-list">
                {messages.map((msg, index) => (
                  <ChatMessage
                    key={msg.id}
                    message={msg}
                    index={index}
                    onItemClick={handleItemClick}
                  />
                ))}

                {isLoading && (
                  <motion.div
                    className="chat-message chat-message-assistant"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="chat-message-avatar">
                      <SparkleIcon />
                    </div>
                    <div className="chat-message-content">
                      {/* COL-39: staged status text once real events land,
                          identical bare bouncing dots until then (or if the
                          viewed session no longer matches the in-flight
                          send — see isViewingSendsSession above). */}
                      <ChatProgress events={isViewingSendsSession ? progressEvents : []} />
                    </div>
                  </motion.div>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ChatErrorBoundary>

        {/* Input Area */}
        <div className="chat-input-container">
          {pinnedItem && (
            <div className="chat-pinned-item" data-testid="pinned-item-chip">
              <button
                type="button"
                className="chat-pinned-item-body"
                onClick={() => handleItemClick(pinnedItem)}
              >
                {pinnedItem.thumbnail && (
                  <img src={pinnedItem.thumbnail} alt="" className="chat-pinned-item-thumb" />
                )}
                <span className="chat-pinned-item-label">
                  Chatting about: <strong>{pinnedItem.headline}</strong>
                </span>
              </button>
              <button
                type="button"
                className="chat-pinned-item-unpin"
                onClick={handleUnpin}
                disabled={isLoading}
                aria-label="Stop chatting about this item"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder={isLoadingSessions ? 'Loading conversations...' : 'Ask about your collection...'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading || isLoadingSessions}
              aria-label="Message input"
            />
            <button
              className="chat-send-btn"
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading || isLoadingSessions}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
          <p className="chat-input-hint">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </main>

      {/* Item Detail Modal */}
      <AnimatePresence>
        {selectedItem && (
          <ItemDetailModal
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            onDelete={() => {}}
            onRefresh={() => {}}
            // Lets a conversation fork onto a new pinned session from a
            // result card's modal, same as the Gallery entry point (COL-93).
            onChatAbout={(item) => {
              setSelectedItem(null);
              pinItemAndStartNewSession(item);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Icons
function MenuIcon() {
  return <List size={20} />;
}

function SearchSparkleIcon() {
  return <MagnifyingGlassPlus size={48} />;
}

function SendIcon() {
  return <PaperPlaneTilt size={18} />;
}
